// mapBuyBox — the map tab's buy box (#3061): narrow the county to the houses an
// investor actually buys, by building facts and value.
//
// Pure (no React), so the matching rules are unit-tested on their own.
//
// The rule that matters most here is UNKNOWN handling. Only ~41% of live
// properties carry square footage, and Wake, Randolph and Rowan never publish
// bedrooms (docs: building-facts gap audit, 2026-10-19). A naive "beds >= 3"
// would silently hide about half the improved houses in the state, so a
// missing value is its own outcome, not a failure:
//   - includeUnknown (the default) keeps the row and counts it in missingKept;
//   - turning it off drops the row and counts it in missingExcluded,
// and the UI shows both counts so the narrowing is never silent.
//
// Vacant land is not "unknown": it has no building, so it can never meet a
// building criterion. Value criteria still apply to it.
//
// Rows outside the viewer's plan are excluded whenever the box is active
// (outsideExcluded). Their value is withheld by the API (assessedValue 0), and
// the box shouldn't be a way to sort data the plan doesn't unlock.

import { BuyBox, BuyBoxInput, Property } from "../types";
import { isVacantLand } from "./propertyUrls";
import { resolveAmountOwed } from "./dataQuality";

export interface MapBuyBox {
  minSqft: number | null;
  maxSqft: number | null;
  minYearBuilt: number | null;
  maxYearBuilt: number | null;
  minBeds: number | null;
  minBaths: number | null;
  minValue: number | null;
  maxValue: number | null;
  /** Dollars: assessed value minus the amount owed, as on the Leads tab. */
  minEquity: number | null;
  /** Dollars owed: unpaid taxes, or the debt figure the panel shows (resolveAmountOwed). */
  minOwed: number | null;
  maxOwed: number | null;
  /** Parcel size in acres (the CAMA lot size). */
  minAcres: number | null;
  maxAcres: number | null;
  /** Assessed land value alone, in dollars. */
  minLandValue: number | null;
  maxLandValue: number | null;
  includeUnknown: boolean;
}

export const EMPTY_MAP_BUY_BOX: MapBuyBox = {
  minSqft: null,
  maxSqft: null,
  minYearBuilt: null,
  maxYearBuilt: null,
  minBeds: null,
  minBaths: null,
  minValue: null,
  maxValue: null,
  minEquity: null,
  minOwed: null,
  maxOwed: null,
  minAcres: null,
  maxAcres: null,
  minLandValue: null,
  maxLandValue: null,
  includeUnknown: true,
};

const BUILDING_KEYS = ["minSqft", "maxSqft", "minYearBuilt", "maxYearBuilt", "minBeds", "minBaths"] as const;
const VALUE_KEYS = [
  "minValue",
  "maxValue",
  "minEquity",
  "minOwed",
  "maxOwed",
  "minAcres",
  "maxAcres",
  "minLandValue",
  "maxLandValue",
] as const;

/** Number of criteria set (includeUnknown is a mode, not a criterion). */
export function mapBuyBoxActiveCount(box: MapBuyBox): number {
  return [...BUILDING_KEYS, ...VALUE_KEYS].filter((k) => box[k] != null).length;
}

export interface MapBuyBoxResult {
  matched: Property[];
  /** Kept because includeUnknown was on, though a criterion couldn't be checked. */
  missingKept: number;
  /** Dropped only because a criterion couldn't be checked (includeUnknown off). */
  missingExcluded: number;
  /** Dropped because the row is outside the viewer's plan. */
  outsideExcluded: number;
}

type Check = "pass" | "fail" | "unknown";

function positive(n: number | null | undefined): number | null {
  return n != null && Number.isFinite(n) && n > 0 ? n : null;
}

function inRange(v: number | null, min: number | null, max: number | null): Check {
  if (min == null && max == null) return "pass";
  if (v == null) return "unknown";
  if (min != null && v < min) return "fail";
  if (max != null && v > max) return "fail";
  return "pass";
}

/** Full baths plus half a bath per half bath; null when neither is on file. */
export function bathsOf(p: Property): number | null {
  const full = positive(p.building?.fullBaths) ?? 0;
  const half = positive(p.building?.halfBaths) ?? 0;
  const total = full + 0.5 * half;
  return total > 0 ? total : null;
}

/**
 * Assessed value minus amount owed, or null when either is missing.
 *
 * The amount owed is resolveAmountOwed's figure: the one the detail panel shows
 * and the deal score uses (#3132). It falls back from a published amount due to
 * the opening bid, then to the deed-of-trust original principal, and treats a 0
 * amountDue as "not published", not a paid-off debt (#1062). No haircut on the
 * original principal here: the score discounts it, but a min-equity filter is a
 * yes/no threshold, and the panel shows the undiscounted figure.
 */
export function equityOf(p: Property): number | null {
  const assessed = positive(p.assessedValue);
  if (assessed == null) return null;
  const owed = resolveAmountOwed(p);
  const due = owed.state === "unavailable" ? null : positive(owed.amount);
  if (due == null) return null;
  return assessed - due;
}

/** The debt figure the panel shows, or null when none is published. */
export function owedOf(p: Property): number | null {
  const owed = resolveAmountOwed(p);
  return owed.state === "unavailable" ? null : positive(owed.amount);
}

function checks(p: Property, box: MapBuyBox): Check[] {
  const out: Check[] = [];
  const wantsBuilding = BUILDING_KEYS.some((k) => box[k] != null);
  if (wantsBuilding && isVacantLand(p)) {
    out.push("fail");
  } else {
    const b = p.building;
    out.push(inRange(positive(b?.totalSqft), box.minSqft, box.maxSqft));
    out.push(inRange(positive(b?.yearBuilt), box.minYearBuilt, box.maxYearBuilt));
    out.push(inRange(positive(b?.bedrooms), box.minBeds, null));
    out.push(inRange(bathsOf(p), box.minBaths, null));
  }
  out.push(inRange(positive(p.assessedValue), box.minValue, box.maxValue));
  out.push(inRange(equityOf(p), box.minEquity, null));
  // Debt and land apply to vacant land too: a lot is exactly what an acreage
  // or land-value filter is for.
  out.push(inRange(owedOf(p), box.minOwed, box.maxOwed));
  out.push(inRange(positive(p.building?.lotSizeAcres), box.minAcres, box.maxAcres));
  out.push(inRange(positive(p.valuation?.landValue), box.minLandValue, box.maxLandValue));
  return out;
}

export function applyMapBuyBox(
  rows: Property[],
  box: MapBuyBox,
  isAllowed: (p: Property) => boolean
): MapBuyBoxResult {
  if (mapBuyBoxActiveCount(box) === 0) {
    return { matched: rows, missingKept: 0, missingExcluded: 0, outsideExcluded: 0 };
  }
  const result: MapBuyBoxResult = { matched: [], missingKept: 0, missingExcluded: 0, outsideExcluded: 0 };
  for (const p of rows) {
    if (!isAllowed(p)) {
      result.outsideExcluded++;
      continue;
    }
    const cs = checks(p, box);
    if (cs.includes("fail")) continue;
    if (cs.includes("unknown")) {
      if (box.includeUnknown) {
        result.matched.push(p);
        result.missingKept++;
      } else {
        result.missingExcluded++;
      }
      continue;
    }
    result.matched.push(p);
  }
  return result;
}

// ── Saved buy boxes on the map ───────────────────────────────────────────
//
// A saved buy box (the Leads tab's "Saved" row, and the email alerts) has
// more criteria than the map applies. Loading one on the map applies what the
// map can check and names the rest, so a box never looks applied when part of
// it isn't.

/** The map's criteria from a saved box, plus the saved criteria the map can't apply. */
export function mapBuyBoxFromSaved(b: BuyBox, countyId: string): { box: MapBuyBox; notApplied: string[] } {
  const box: MapBuyBox = {
    minSqft: b.minSqft ?? null,
    maxSqft: b.maxSqft ?? null,
    minYearBuilt: b.minYearBuilt ?? null,
    maxYearBuilt: b.maxYearBuilt ?? null,
    minBeds: b.minBeds ?? null,
    minBaths: b.minBaths ?? null,
    minValue: b.minAssessedValue ?? null,
    maxValue: b.maxAssessedValue ?? null,
    minEquity: b.minEquity ?? null,
    minOwed: b.minAmountDue ?? null,
    maxOwed: b.maxAmountDue ?? null,
    minAcres: b.minAcres ?? null,
    maxAcres: b.maxAcres ?? null,
    minLandValue: b.minLandValue ?? null,
    maxLandValue: b.maxLandValue ?? null,
    includeUnknown: b.includeUnknownBuilding !== false,
  };
  const notApplied: string[] = [];
  if ((b.signalTypes || []).length > 0) notApplied.push("lead type");
  if ((b.propertyClasses || []).length > 0) notApplied.push("property class");
  if (b.absenteeOnly) notApplied.push("absentee owner");
  if (b.entityOnly) notApplied.push("LLC or company owner");
  const counties = b.counties || [];
  if (counties.length > 0 && !counties.includes(countyId)) notApplied.push("counties (it's saved for others)");
  return { box, notApplied };
}

/**
 * A new saved box from the map's criteria, scoped to the county on screen.
 * An empty county list means every county, so a box saved while looking at
 * one county would otherwise match (and email) the whole state.
 */
export function mapBuyBoxToInput(name: string, box: MapBuyBox, countyId: string): BuyBoxInput {
  return {
    name,
    counties: countyId ? [countyId] : [],
    signalTypes: [],
    propertyClasses: [],
    minAssessedValue: box.minValue,
    maxAssessedValue: box.maxValue,
    minEquity: box.minEquity,
    minAcres: box.minAcres,
    maxAcres: box.maxAcres,
    absenteeOnly: false,
    entityOnly: false,
    minSqft: box.minSqft,
    maxSqft: box.maxSqft,
    minYearBuilt: box.minYearBuilt,
    maxYearBuilt: box.maxYearBuilt,
    minBeds: box.minBeds,
    minBaths: box.minBaths,
    includeUnknownBuilding: box.includeUnknown,
    minAmountDue: box.minOwed,
    maxAmountDue: box.maxOwed,
    minLandValue: box.minLandValue,
    maxLandValue: box.maxLandValue,
  };
}
